// BlockedDemandsSection — Reforma UX §6.8 / wireframe pp-run "Demandas
// bloqueadas" (PIPE-06).
//
// POSIÇÃO CANÔNICA (HTML vence): dentro da MODAL do pipeline, aba Runs, ACIMA
// de "Runs recentes" — cards de demandas bloqueadas DESTE pipeline com
// "Rodar demanda" (reprocessar) + "Ver detalhes" (caminho mais simples:
// navega pra /mc/demands, onde o painel de detalhe vive).
//
// Dados: GET /v1/demands?status=blocked (api.listDemands) filtrado
// client-side por pipeline_slug. O payload de demand NÃO traz "bloqueada na
// etapa X (n/m)" nem o motivo do bloqueio — divergência com o wireframe
// reportada; mostramos código + título + descrição.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Play, Eye, Package } from 'lucide-react';
import { api, type Demand } from '@/lib/api';

export interface BlockedDemandsSectionProps {
  pipelineSlug: string;
  onRunDemand: (demand: Demand) => void;
}

interface LoadState {
  /** Slug pro qual o fetch foi feito — loading = state.slug !== prop atual. */
  slug: string;
  demands: Demand[];
  error: string;
}

export function BlockedDemandsSection({ pipelineSlug, onRunDemand }: BlockedDemandsSectionProps) {
  const navigate = useNavigate();
  // Envelope único setado SÓ em callbacks async (lint react-hooks/set-state-
  // in-effect proíbe setState síncrono no corpo do effect). loading é
  // derivado: dado carregado pertence a outro slug → ainda carregando.
  const [state, setState] = useState<LoadState | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.listDemands({ status: 'blocked', per_page: 100 })
      .then((res) => {
        if (cancelled) return;
        const items = res.items ?? res.demands ?? [];
        setState({
          slug: pipelineSlug,
          demands: items.filter((d) => d.pipeline_slug === pipelineSlug),
          error: '',
        });
      })
      .catch((err: Error) => {
        if (!cancelled) setState({ slug: pipelineSlug, demands: [], error: err.message });
      });
    return () => { cancelled = true; };
  }, [pipelineSlug]);

  const current = state && state.slug === pipelineSlug ? state : null;
  const loading = current === null;
  const demands = current?.demands ?? [];
  const error = current?.error ?? '';

  return (
    <div>
      <h4 className="text-sm font-semibold text-raios-text mb-2.5 flex items-center gap-2">
        Demandas bloqueadas
        <Badge variant="warning" className="font-mono text-[10px]">{demands.length}</Badge>
      </h4>

      {loading && <Skeleton className="h-20 w-full" />}
      {error && <p className="text-xs text-raios-danger-text">Erro ao carregar demandas bloqueadas: {error}</p>}

      {!loading && !error && (
        demands.length === 0 ? (
          <p className="text-xs text-raios-muted mb-2">Nenhuma demanda bloqueada neste pipeline.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {demands.map((d) => (
              <Card key={d.id} className="p-4 border border-raios-warning/30 flex flex-col gap-2">
                <div className="flex items-center gap-1.5 text-xs font-mono text-raios-warning-text">
                  <Package className="h-3.5 w-3.5" />
                  {d.code ?? d.id.slice(0, 8)}
                </div>
                <p className="text-sm font-medium text-raios-text leading-snug">{d.title}</p>
                {d.description && (
                  <p className="text-xs text-raios-muted line-clamp-2">{d.description}</p>
                )}
                <div className="flex items-center gap-2 mt-auto pt-1.5">
                  <Button size="sm" variant="brand" onClick={() => onRunDemand(d)}>
                    <Play className="h-3.5 w-3.5" />
                    Rodar demanda
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate('/mc/demands')}
                    title="Abrir Mission Control · Demandas"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Ver detalhes
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )
      )}
    </div>
  );
}
